import { useCallback, useEffect, useRef, useState } from 'react'
import { authAPI, getSessionExpiresAt, setSessionExpiresAt } from '@/api/client'
import { useAuthStore } from '@/stores/auth'

const HEARTBEAT_INTERVAL = 4 * 60 * 1000 // 4 minutes
const IDLE_THRESHOLD = 15 * 60 * 1000 // 15 minutes
const TICK_INTERVAL = 1000 // 1 second
const WARNING_THRESHOLD = 5 * 60 // 5 minutes in seconds

const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'mousedown', 'scroll', 'touchstart', 'click'] as const

export function useSessionTimeout() {
  const [isWarning, setIsWarning] = useState(false)
  const [isExpired, setIsExpired] = useState(false)
  const [remainingSeconds, setRemainingSeconds] = useState(Infinity)

  // Seeded by the mount effect below, not here: reading a clock during render
  // is impure and writing a ref during render is a render-phase write, and the
  // React compiler refuses both.
  const lastActivityRef = useRef(0)
  const isActiveRef = useRef(false)
  const heartbeatTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const tickTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const expiresAtOverrideRef = useRef<string | null>(null)
  const warningDismissedRef = useRef(false)
  const wasExpiredRef = useRef(false)

  const sendHeartbeat = useCallback(async () => {
    const idleTime = Date.now() - lastActivityRef.current
    if (idleTime > IDLE_THRESHOLD) {
      isActiveRef.current = false
      return
    }
    try {
      const response = await authAPI.heartbeat()
      const expiresAt = response.headers?.['x-session-expires-at']
      if (expiresAt) {
        setSessionExpiresAt(expiresAt)
        expiresAtOverrideRef.current = expiresAt
      }
    } catch {
      // Heartbeat failure is non-critical
    }
  }, [])

  const stayLoggedIn = useCallback(async () => {
    // Immediately dismiss warning — don't wait for heartbeat response
    warningDismissedRef.current = true
    wasExpiredRef.current = false
    setIsWarning(false)
    setIsExpired(false)
    lastActivityRef.current = Date.now()
    isActiveRef.current = true
    try {
      const response = await authAPI.heartbeat()
      const expiresAt = response.headers?.['x-session-expires-at']
      if (expiresAt) {
        setSessionExpiresAt(expiresAt)
        expiresAtOverrideRef.current = expiresAt
      }
    } catch {
      // If heartbeat fails during stay-logged-in, session is truly gone
      useAuthStore.getState().logout()
    }
  }, [])

  // Stamp the mount time as the first "activity". Runs before the heartbeat
  // interval below is created, so no reader ever sees the unseeded 0.
  useEffect(() => {
    lastActivityRef.current = Date.now()
  }, [])

  // Tick: compute remaining time and update warning/expired state
  useEffect(() => {
    tickTimerRef.current = setInterval(() => {
      const expiresAtStr = expiresAtOverrideRef.current || getSessionExpiresAt()
      if (!expiresAtStr) return

      const expiresAt = new Date(expiresAtStr).getTime()
      const now = Date.now()
      const remaining = Math.max(0, Math.floor((expiresAt - now) / 1000))

      setRemainingSeconds(remaining)

      if (remaining <= 0) {
        warningDismissedRef.current = false
        // Auto-logout on the false→true expiry edge (once per transition,
        // matching the previous effect that watched `isExpired`).
        if (!wasExpiredRef.current) {
          wasExpiredRef.current = true
          useAuthStore.getState().logout()
        }
        setIsExpired(true)
        setIsWarning(false)
      } else if (remaining <= WARNING_THRESHOLD) {
        wasExpiredRef.current = false
        if (!warningDismissedRef.current) {
          setIsWarning(true)
        }
        setIsExpired(false)
      } else {
        wasExpiredRef.current = false
        warningDismissedRef.current = false
        setIsWarning(false)
        setIsExpired(false)
      }
    }, TICK_INTERVAL)

    return () => {
      if (tickTimerRef.current) clearInterval(tickTimerRef.current)
    }
  }, [])

  // Heartbeat interval: only sends when user is active
  useEffect(() => {
    heartbeatTimerRef.current = setInterval(() => {
      if (isActiveRef.current) {
        sendHeartbeat()
      }
    }, HEARTBEAT_INTERVAL)

    return () => {
      if (heartbeatTimerRef.current) clearInterval(heartbeatTimerRef.current)
    }
  }, [sendHeartbeat])

  // Activity event listeners
  useEffect(() => {
    const handleActivity = () => {
      lastActivityRef.current = Date.now()
      isActiveRef.current = true
    }
    for (const event of ACTIVITY_EVENTS) {
      // passive: we never preventDefault, so scroll/touch must not wait on us
      window.addEventListener(event, handleActivity, { passive: true })
    }
    return () => {
      for (const event of ACTIVITY_EVENTS) {
        window.removeEventListener(event, handleActivity)
      }
    }
  }, [])

  return { isWarning, isExpired, remainingSeconds, stayLoggedIn }
}
